import { useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useAppDispatch, useAppSelector } from '@/store';
import type { RootState } from '@/store';
import { fetchCiternes } from '@/store/thunks/citerneThunks';
import { fetchPompistes } from '@/store/thunks/pompisteThunks';
import { createJauge, updateJauge } from '@/store/thunks/jaugeThunks';
import type { Jauge } from '@/types';
import type { Citerne } from '@/types';
import type { Pompiste } from '@/types';
import { Input, Button, Select } from '@/components/ui';

const schema = Yup.object({
  citerneId: Yup.string().required('Citerne obligatoire'),
  pompisteId: Yup.string().required('Pompiste obligatoire'),
  date: Yup.string().required('Date obligatoire'),
  valeur: Yup.number().transform((_, o) => (o === '' || o == null ? undefined : Number(o))).required('Valeur obligatoire'),
  temperateure: Yup.number().transform((_, o) => (o === '' || o == null ? undefined : Number(o))).required('Température obligatoire'),
});

interface JaugerFormProps {
  jauge?: Jauge | null;
  onClose: () => void;
  onSuccess?: () => void;
}

export default function JaugerForm({ jauge, onClose, onSuccess }: JaugerFormProps) {
  const dispatch = useAppDispatch();
  const citernes = useAppSelector((s: RootState) => s.citerne.citernes);
  const pumpistes = useAppSelector((s: RootState) => s.pompiste.pumpistes);

  useEffect(() => {
    dispatch(fetchCiternes());
    dispatch(fetchPompistes());
  }, [dispatch]);

  const formik = useFormik({
    initialValues: {
      citerneId: jauge?.citerne ? String((jauge.citerne as { id?: number }).id) : '',
      pompisteId: jauge?.pompiste ? String((jauge.pompiste as { id?: number }).id) : '',
      date: jauge?.date ? (jauge.date as string).slice(0, 10) : new Date().toISOString().slice(0, 10),
      valeur: jauge?.valeur ?? '',
      temperateure: jauge?.temperateure ?? '',
    },
    validationSchema: schema,
    onSubmit: async (values) => {
      const citerne = citernes.find((c: Citerne) => String(c.id) === values.citerneId);
      const pompiste = pumpistes.find((p: Pompiste) => String(p.id) === values.pompisteId);
      if (!citerne || !pompiste) return;
      const payload = {
        citerne: { id: citerne.id, code: citerne.code },
        pompiste: { id: pompiste.id, nom: pompiste.nom },
        date: values.date,
        valeur: Number(values.valeur),
        temperateure: Number(values.temperateure),
      };
      if (jauge?.id) {
        const res = await dispatch(updateJauge({ ...jauge, ...payload }));
        if (updateJauge.fulfilled.match(res)) {
          onSuccess?.();
          onClose();
        }
      } else {
        const res = await dispatch(createJauge(payload));
        if (createJauge.fulfilled.match(res)) {
          onSuccess?.();
          onClose();
        }
      }
    },
  });

  const citerneOptions = citernes.map((c: Citerne) => ({ value: String(c.id), label: c.code ?? String(c.id) }));
  const pompisteOptions = pumpistes.map((p: Pompiste) => ({ value: String(p.id), label: `${p.nom ?? ''} ${p.prenom ?? ''}`.trim() || String(p.id) }));

  return (
    <form onSubmit={formik.handleSubmit} className="space-y-4">
      <Select label="Citerne" options={citerneOptions} value={formik.values.citerneId} onChange={(e) => formik.setFieldValue('citerneId', e.target.value)} onBlur={formik.handleBlur} error={formik.touched.citerneId ? formik.errors.citerneId : undefined} />
      <Select label="Pompiste" options={pompisteOptions} value={formik.values.pompisteId} onChange={(e) => formik.setFieldValue('pompisteId', e.target.value)} onBlur={formik.handleBlur} error={formik.touched.pompisteId ? formik.errors.pompisteId : undefined} />
      <Input label="Date" type="date" id="date" {...formik.getFieldProps('date')} error={formik.touched.date ? formik.errors.date : undefined} />
      <Input label="Valeur" type="number" id="valeur" {...formik.getFieldProps('valeur')} error={formik.touched.valeur ? formik.errors.valeur : undefined} />
      <Input label="Température" type="number" id="temperateure" {...formik.getFieldProps('temperateure')} error={formik.touched.temperateure ? formik.errors.temperateure : undefined} />
      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="outline" onClick={onClose}>Annuler</Button>
        <Button type="submit" loading={formik.isSubmitting}>{jauge?.id ? 'Modifier' : 'Créer'}</Button>
      </div>
    </form>
  );
}
